import * as THREE from 'three';
import {
  BUILDING_WALL_COLOR, BUILDING_ROOF_COLOR,
  BUILDING_DOOR_COLOR, BUILDING_WINDOW_GLOW,
  BUILDING_WIDTH, BUILDING_HEIGHT, BUILDING_DEPTH,
} from '@/utils/constants';

/**
 * Creates the office building with walls, roof, glowing windows, and door.
 * The building is the click target for entering the interior.
 */
export function createBuilding(): THREE.Group {
  const group = new THREE.Group();
  group.name = 'building';
  group.userData = { interactive: true, action: 'enter', id: 'building' };

  // Enable layer 1 for raycasting
  group.layers.enable(1);

  // ── Main Walls ───────────────────────────────────────────
  const wallGeo = new THREE.BoxGeometry(BUILDING_WIDTH, BUILDING_HEIGHT, BUILDING_DEPTH);
  const wallMat = new THREE.MeshStandardMaterial({
    color: BUILDING_WALL_COLOR,
    roughness: 0.6,
  });
  const walls = new THREE.Mesh(wallGeo, wallMat);
  walls.position.y = BUILDING_HEIGHT / 2;
  walls.castShadow = true;
  walls.receiveShadow = true;
  walls.layers.enable(1);
  group.add(walls);

  // ── Roof ─────────────────────────────────────────────────
  const roofGeo = new THREE.BoxGeometry(BUILDING_WIDTH + 0.3, 0.15, BUILDING_DEPTH + 0.3);
  const roofMat = new THREE.MeshStandardMaterial({
    color: BUILDING_ROOF_COLOR,
    roughness: 0.5,
  });
  const roof = new THREE.Mesh(roofGeo, roofMat);
  roof.position.y = BUILDING_HEIGHT + 0.075;
  roof.castShadow = true;
  roof.layers.enable(1);
  group.add(roof);

  // ── Door ─────────────────────────────────────────────────
  const doorGeo = new THREE.PlaneGeometry(0.8, 1.4);
  const doorMat = new THREE.MeshStandardMaterial({
    color: BUILDING_DOOR_COLOR,
    roughness: 0.7,
  });
  const door = new THREE.Mesh(doorGeo, doorMat);
  door.position.set(0, 0.7, BUILDING_DEPTH / 2 + 0.01);
  door.layers.enable(1);
  group.add(door);

  // Door frame
  const frameGeo = new THREE.PlaneGeometry(0.9, 1.5);
  const frameMat = new THREE.MeshStandardMaterial({ color: 0x3a2a1a });
  const frame = new THREE.Mesh(frameGeo, frameMat);
  frame.position.set(0, 0.75, BUILDING_DEPTH / 2 + 0.005);
  group.add(frame);

  // ── Windows ──────────────────────────────────────────────
  const windowPositions = [
    new THREE.Vector3(-1.2, 1.8, BUILDING_DEPTH / 2 + 0.01),
    new THREE.Vector3(1.2, 1.8, BUILDING_DEPTH / 2 + 0.01),
    // Side window
    new THREE.Vector3(BUILDING_WIDTH / 2 + 0.01, 1.8, 0),
  ];

  windowPositions.forEach((pos, i) => {
    const winGeo = new THREE.PlaneGeometry(0.6, 0.5);
    const winMat = new THREE.MeshStandardMaterial({
      color: BUILDING_WINDOW_GLOW,
      emissive: BUILDING_WINDOW_GLOW,
      emissiveIntensity: 0.8,
      roughness: 0.3,
    });
    const win = new THREE.Mesh(winGeo, winMat);
    win.position.copy(pos);
    // Rotate side window
    if (i === 2) {
      win.rotation.y = -Math.PI / 2;
    }
    win.name = `window-${i}`;
    group.add(win);
  });

  return group;
}

/**
 * Apply hover highlight to building (slight emissive increase).
 */
export function highlightBuilding(building: THREE.Group, highlight: boolean): void {
  building.traverse((child) => {
    if (child instanceof THREE.Mesh && child.material instanceof THREE.MeshStandardMaterial) {
      // Skip windows — they already glow
      if (child.name.startsWith('window-')) return;
      if (highlight) {
        child.material.emissive.setHex(0x222222);
        child.material.emissiveIntensity = 0.3;
      } else {
        child.material.emissive.setHex(0x000000);
        child.material.emissiveIntensity = 0;
      }
    }
  });
}
